import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';

import { WhiteboardErrorCode, useUploadWhiteboardFile } from 'core';
import type { IWhiteboardFile } from 'core';

import type {
  BinaryFileData,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from '@excalidraw/excalidraw/types/types';

interface IUseWhiteboardImages {
  apiRef: MutableRefObject<ExcalidrawImperativeAPI | null>;
  apiReady: boolean;
  /** S3 files from the board query — the hydration source. */
  files: IWhiteboardFile[];
  /**
   * Omit in read-only viewers: hydration still runs (students and methodists
   * must see the images), uploads are disabled.
   */
  whiteboardId?: string;
}

/** excalidraw brands `id`/`dataURL`; the cast keeps that noise at this boundary. */
const toBinaryFile = (fileId: string, dataURL: string, mimeType: string): BinaryFileData =>
  ({ id: fileId, dataURL, mimeType, created: Date.now() } as unknown as BinaryFileData);

const readAsDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** "image/svg+xml" → "svg"; the filename is cosmetic, the server trusts mimeType. */
const extensionFor = (mimeType: string): string => {
  const subtype = mimeType.split('/')[1];
  return subtype ? subtype.split('+')[0] : 'png';
};

/**
 * Scene images live on S3, not inside sceneJson (spec 0010 rev.3): excalidraw
 * keeps pasted images as base64 dataURLs, and a few screenshots blow past the
 * server's 8MB scene cap. Two halves:
 *
 *  - HYDRATION (editor AND viewer): fetch each presigned url → dataURL →
 *    `addFiles`, so the fileId references on the elements resolve to pixels.
 *  - UPLOAD (editor only): each newly pasted id is pushed to S3 immediately.
 *
 * Ordering: the upload fires on `onChange` UNDEBOUNCED while the scene save is
 * debounced 2s, so an image reaches S3 before the scene that references it.
 * Even if a save landed first, nothing is lost — the server prunes only files
 * whose fileId no live element references, and the element is already there.
 *
 * Echo suppression: `addFiles` itself triggers an `onChange` carrying the
 * hydrated ids. `uploadedIdsRef` is seeded with the server ids SYNCHRONOUSLY,
 * before the fetch is awaited, so a hydrated image can never be re-uploaded.
 *
 * Nothing here ever blocks drawing: every fetch/upload is guarded and failures
 * degrade to a warning.
 */
export const useWhiteboardImages = ({
  apiRef,
  apiReady,
  files,
  whiteboardId,
}: IUseWhiteboardImages) => {
  const { t } = useTranslation();
  const { uploadFile } = useUploadWhiteboardFile();

  /** Claimed (fetch started) — never re-fetched, success or failure. */
  const hydratedIdsRef = useRef<Set<string>>(new Set());
  /**
   * Settled ids: uploaded OK, hydrated from the server, or permanently
   * rejected (FILE_TOO_LARGE / INVALID_FILE — resending the same bytes can
   * only fail again, and re-sending 12MB on every stroke would be worse than
   * the missing image). Transient failures stay OUT so a later change retries.
   */
  const uploadedIdsRef = useRef<Set<string>>(new Set());
  const inFlightIdsRef = useRef<Set<string>>(new Set());
  const loadWarnedRef = useRef(false);
  const uploadWarnedRef = useRef(false);

  // ---- (a) Hydration -------------------------------------------------------
  useEffect(() => {
    if (!apiReady || !apiRef.current || !files.length) {
      return undefined;
    }
    const pending = files.filter((file) => !hydratedIdsRef.current.has(file.fileId));
    if (!pending.length) {
      return undefined;
    }
    // Claim BEFORE any await: the query re-renders with a fresh `files` array
    // identity, and the addFiles echo must already find these ids settled.
    pending.forEach((file) => {
      hydratedIdsRef.current.add(file.fileId);
      uploadedIdsRef.current.add(file.fileId);
    });

    let cancelled = false;
    (async () => {
      const loaded: BinaryFileData[] = [];
      let anyFailed = false;

      await Promise.all(
        pending.map(async (file) => {
          try {
            const response = await fetch(file.url);
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const dataURL = await readAsDataUrl(blob);
            loaded.push(toBinaryFile(file.fileId, dataURL, file.mimeType || blob.type));
          } catch (error) {
            // An expired/dead presigned link must never break the board: that
            // element renders as a placeholder, everything else still draws.
            anyFailed = true;
            console.warn(`whiteboard: failed to load image ${file.fileId}`, error);
          }
        }),
      );

      if (cancelled) {
        return;
      }
      if (loaded.length) {
        apiRef.current?.addFiles(loaded);
      }
      if (anyFailed && !loadWarnedRef.current) {
        loadWarnedRef.current = true;
        message.warning(t('whiteboard.images.loadFailed'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiReady, apiRef, files, t]);

  // ---- (b) Upload (editor only) --------------------------------------------
  const uploadOne = useCallback(
    async (binary: BinaryFileData) => {
      if (!whiteboardId) {
        return;
      }
      const fileId = String(binary.id);
      try {
        // fetch() parses data: URLs natively — cheaper and safer than
        // hand-rolling atob + Uint8Array.
        const response = await fetch(String(binary.dataURL));
        const blob = await response.blob();
        const mimeType = binary.mimeType || blob.type || 'image/png';
        const payload = new File([blob], `${fileId}.${extensionFor(mimeType)}`, {
          type: mimeType,
        });

        const result = await uploadFile(whiteboardId, fileId, payload, mimeType);
        if (result.success) {
          uploadedIdsRef.current.add(fileId);
          uploadWarnedRef.current = false;
          return;
        }

        if (result.errorCode === WhiteboardErrorCode.FILE_TOO_LARGE) {
          uploadedIdsRef.current.add(fileId);
          message.warning(t('whiteboard.images.tooLarge'));
          return;
        }
        if (result.errorCode === WhiteboardErrorCode.INVALID_FILE) {
          uploadedIdsRef.current.add(fileId);
          if (!uploadWarnedRef.current) {
            uploadWarnedRef.current = true;
            message.warning(t('whiteboard.images.uploadFailed'));
          }
          return;
        }
        // Transient (NOT_LESSON_TEACHER included — the teacher may have just
        // been re-linked): stay unclaimed so the next change retries.
        if (!uploadWarnedRef.current) {
          uploadWarnedRef.current = true;
          message.warning(t('whiteboard.images.uploadFailed'));
        }
      } catch {
        // Network error — leave the id unclaimed; the next onChange retries.
        if (!uploadWarnedRef.current) {
          uploadWarnedRef.current = true;
          message.warning(t('whiteboard.images.uploadFailed'));
        }
      } finally {
        inFlightIdsRef.current.delete(fileId);
      }
    },
    [t, uploadFile, whiteboardId],
  );

  /**
   * Fed from the editor's excalidraw `onChange`. Fires uploads immediately —
   * deliberately NOT debounced, see the ordering note above.
   */
  const onImagesChange = useCallback(
    (sceneFiles: BinaryFiles) => {
      if (!whiteboardId) {
        return;
      }
      Object.values(sceneFiles || {}).forEach((binary) => {
        if (!binary?.dataURL) {
          return;
        }
        const fileId = String(binary.id);
        if (uploadedIdsRef.current.has(fileId) || inFlightIdsRef.current.has(fileId)) {
          return;
        }
        inFlightIdsRef.current.add(fileId);
        // Fire-and-forget: onChange runs on every stroke and must never await.
        // uploadOne swallows its own errors, so this can't reject.
        uploadOne(binary);
      });
    },
    [uploadOne, whiteboardId],
  );

  return { onImagesChange };
};
